import {
	getCoreRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { useTranslate } from "@tolgee/react";
import { Download } from "lucide-react";
import { useCallback } from "react";
import type { UserFundingUpdate } from "#/hooks/use-hyperliquid-account";
import { FUNDING_HISTORY_GRID_CLASS } from "./constants/positions.constants.ts";
import { fundingHistoryColumns } from "./funding-history-columns.tsx";
import { downloadCsv } from "./lib/download-csv.ts";
import { VirtualizedGridTable } from "./virtualized-grid-table.tsx";

const coreRowModel = getCoreRowModel();
const sortedRowModel = getSortedRowModel();

type FundingHistoryTableProps = {
	fundingHistory: UserFundingUpdate[];
	fetchNextPage: () => void;
	hasNextPage: boolean;
	isFetchingNextPage: boolean;
	isError?: boolean;
	onRetry?: () => void;
};

export function FundingHistoryTable({
	fundingHistory,
	fetchNextPage,
	hasNextPage,
	isFetchingNextPage,
	isError,
	onRetry,
}: FundingHistoryTableProps) {
	const { t } = useTranslate();

	const table = useReactTable({
		data: fundingHistory,
		columns: fundingHistoryColumns,
		getCoreRowModel: coreRowModel,
		getSortedRowModel: sortedRowModel,
	});

	const handleExportCsv = useCallback(() => {
		if (fundingHistory.length === 0) return;
		const headers = [
			"Time",
			"Coin",
			"Funding Rate",
			"Position Size",
			"Payment (USDC)",
		];
		const rows = fundingHistory.map((f) => [
			new Date(f.time).toISOString(),
			f.delta.coin,
			f.delta.fundingRate,
			f.delta.szi,
			f.delta.usdc,
		]);
		downloadCsv(
			`funding-history-${new Date().toISOString().slice(0, 10)}.csv`,
			headers,
			rows,
		);
	}, [fundingHistory]);

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<VirtualizedGridTable
				table={table}
				gridClass={FUNDING_HISTORY_GRID_CLASS}
				estimateSize={36}
				emptyMessage={t("positions.no-funding")}
				onScrollNearEnd={hasNextPage ? fetchNextPage : undefined}
				isLoadingMore={isFetchingNextPage}
				isError={isError}
				onRetry={onRetry}
			/>
			{fundingHistory.length > 0 && (
				<div className="flex shrink-0 items-center border-t border-trade-border-subtle px-4 py-1">
					<button
						type="button"
						onClick={handleExportCsv}
						className="flex items-center gap-1.5 text-caption1 text-trade-text-secondary hover:text-trade-text transition-colors cursor-pointer"
					>
						<Download className="size-3" />
						{t("export-csv")}
					</button>
				</div>
			)}
		</div>
	);
}
